import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, TrendingUp, AlertCircle, BookOpen } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { API } from '../../App';
import { DISPLAY_UNITS } from '../../utils/energyUnits';
import useLegendToggle from '../../utils/useLegendToggle';

// Energy unit options accepted by the backend trends endpoint (subset of DISPLAY_UNITS).
const TARGET_UNIT_OPTIONS = ['GJ', 'kWh', 'MWh', 'MJ', 'MMBtu', 'Gcal'];

const ENERGY_TYPE_OPTIONS = [
  { id: 'electricity', label: 'Electricity', color: '#eab308' },
  { id: 'fuels', label: 'Fuels', color: '#f97316' },
  { id: 'thermal', label: 'Heating / Cooling / Steam', color: '#ef4444' },
  { id: 'water', label: 'Water', color: '#3b82f6' },
  { id: 'waste', label: 'Waste', color: '#6b7280' },
];

// Stable palette by series-key prefix so renewable/non-renewable bands share a hue.
const SERIES_COLORS = {
  electricity: '#eab308',
  electricity_renewable: '#16a34a',
  fuels: '#f97316',
  fuels_renewable: '#65a30d',
  thermal_purchased_heating: '#dc2626',
  thermal_self_generated_heating: '#fca5a5',
  thermal_purchased_cooling: '#0ea5e9',
  thermal_self_generated_cooling: '#bae6fd',
  thermal_purchased_steam: '#7c3aed',
  thermal_self_generated_steam: '#ddd6fe',
  water_fresh: '#3b82f6',
  water_recycled: '#22d3ee',
  waste: '#6b7280',
};

const colorFor = (key, fallbackIdx) => {
  if (SERIES_COLORS[key]) return SERIES_COLORS[key];
  const palette = ['#0891b2', '#a16207', '#be185d', '#1e40af', '#15803d', '#9333ea'];
  return palette[fallbackIdx % palette.length];
};

const formatNumber = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const ERSTrends = ({ embedded = false }) => {
  const { studyId } = useParams();
  const navigate = useNavigate();

  // Filters
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(ENERGY_TYPE_OPTIONS.map(t => t.id));
  const [targetUnit, setTargetUnit] = useState('GJ');
  const [chartType, setChartType] = useState('bar');  // 'bar' (stacked) or 'line'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [study, setStudy] = useState(null);

  // Clickable legend — toggle visibility per series. Series keys are unique
  // across all four charts on this page so a single hook state is safe.
  const { onLegendClick, isHidden } = useLegendToggle();
  const legendFormatter = (value, entry) => {
    const key = entry?.dataKey ?? value;
    const dim = isHidden(key);
    return (
      <span
        style={{
          opacity: dim ? 0.4 : 1,
          textDecoration: dim ? 'line-through' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        title={dim ? 'Click to show' : 'Click to hide'}
      >
        {value}
      </span>
    );
  };
  const legendProps = { wrapperStyle: { fontSize: 11 }, onClick: onLegendClick, formatter: legendFormatter };

  const fetchTrends = async () => {
    if (!studyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      if (selectedTypes.length > 0 && selectedTypes.length < ENERGY_TYPE_OPTIONS.length) {
        params.append('energy_types', selectedTypes.join(','));
      }
      if (targetUnit) params.append('target_unit', targetUnit);

      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/ers/data/analytics/trends/${studyId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load trends');
    } finally {
      setLoading(false);
    }
  };

  // Load study metadata so the header can show the study name + site name
  // instead of a raw UUID.
  useEffect(() => {
    if (!studyId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ers/studies/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStudy(res.data);
      } catch (_e) {
        // Silently fall back to showing the UUID — not worth a toast.
      }
    })();
    return () => { cancelled = true; };
  }, [studyId]);

  useEffect(() => { fetchTrends(); }, [studyId]);  // intentionally only on studyId — filters fire via Apply button

  const toggleType = (id) => {
    setSelectedTypes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Reshape series-by-key into Recharts data: [{month, key1: v, key2: v, ...}, ...]
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.months.map((month, i) => {
      const row = { month };
      for (const s of data.series) row[s.key] = s.values[i] ?? 0;
      return row;
    });
  }, [data]);

  // Re-roll the totals cards client-side so they reflect the legend toggle.
  // Mirrors the backend rollup in routes/ers_data.py: only series whose unit matches
  // the target energy unit (or kL/MT for water/waste) contribute, and the base type
  // is the prefix before the first underscore. `*_renewable` series are skipped because
  // they are subsets of their base series — counting both would double-count.
  const totalsByType = useMemo(() => {
    if (!data) return {};
    const totals = {};
    for (const s of data.series) {
      if (isHidden(s.key)) continue;
      if (s.key.endsWith('_renewable')) continue;
      const isCountable = s.unit === data.target_unit || s.unit === 'kL' || s.unit === 'MT';
      if (!isCountable) continue;
      const base = s.key.includes('_') ? s.key.split('_')[0] : s.key;
      totals[base] = (totals[base] || 0) + (s.total || 0);
    }
    return totals;
  }, [data, isHidden]);

  // Split series by unit so we can chart energy-normalized ones together; native ones (kL, MT) separately.
  const { energySeries, waterSeries, wasteSeries, nativeThermal } = useMemo(() => {
    const out = { energySeries: [], waterSeries: [], wasteSeries: [], nativeThermal: [] };
    if (!data) return out;
    for (const s of data.series) {
      if (s.unit === 'kL') out.waterSeries.push(s);
      else if (s.unit === 'MT') out.wasteSeries.push(s);
      else if (s.unit === data.target_unit) out.energySeries.push(s);
      else out.nativeThermal.push(s);
    }
    return out;
  }, [data]);

  const ChartComponent = chartType === 'line' ? LineChart : BarChart;

  // Standalone mode renders its own page header (back arrow + study name).
  // Embedded mode (inside ERSMain's tab strip) drops that chrome because the
  // surrounding study header already shows the study name + back affordance.
  const header = embedded ? (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">Energy &amp; Resource Trends</h2>
      </div>
      <button
        onClick={() => navigate('/ers/methodology')}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        title="Methodology, standards and assumptions"
      >
        <BookOpen className="w-4 h-4" /> Methodology
      </button>
    </div>
  ) : (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <TrendingUp className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Energy &amp; Resource Trends</h1>
            <p className="text-xs text-gray-500">
              {study?.study_name || `Study ${studyId}`}
              {study?.site_name ? <span className="text-gray-400"> · {study.site_name}</span> : null}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/ers/methodology')}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          title="Methodology, standards and assumptions"
        >
          <BookOpen className="w-4 h-4" /> Methodology
        </button>
      </div>
    </div>
  );

  const body = (
    <div className={embedded ? 'space-y-6' : 'max-w-7xl mx-auto px-6 py-6 space-y-6'}>
        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period start (YYYY-MM)</label>
              <input
                type="month"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period end (YYYY-MM)</label>
              <input
                type="month"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Energy unit (normalization target)</label>
              <select
                value={targetUnit}
                onChange={(e) => setTargetUnit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                title="All energy series are converted to this unit. Water/waste stay in kL/MT."
              >
                {TARGET_UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Chart type</label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="bar">Stacked bar</option>
                <option value="line">Line</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Energy types</label>
            <div className="flex flex-wrap gap-2">
              {ENERGY_TYPE_OPTIONS.map(opt => {
                const active = selectedTypes.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    onClick={() => toggleType(opt.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      active
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-500'
                    }`}
                    style={active ? { borderColor: opt.color, color: opt.color } : {}}
                  >
                    <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: opt.color }}></span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={fetchTrends}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              {loading ? 'Loading…' : 'Apply'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800 text-sm">Could not load trends</p>
              <p className="text-xs text-red-700">{String(error)}</p>
            </div>
          </div>
        )}

        {/* Totals cards — reflect legend toggle in real time */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {ENERGY_TYPE_OPTIONS.map(opt => {
              const total = totalsByType[opt.id];
              const unit = (opt.id === 'water') ? 'kL' : (opt.id === 'waste') ? 'MT' : data.target_unit;
              return (
                <div key={opt.id} className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }}></span>
                    <span className="text-xs text-gray-500">{opt.label}</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{formatNumber(total ?? 0)}</p>
                  <p className="text-xs text-gray-400">{unit}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Energy chart */}
        {data && energySeries.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Energy consumption ({data.target_unit})
            </h2>
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: data.target_unit, angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
                  <Legend {...legendProps} />
                  {energySeries.map((s, i) => (
                    chartType === 'line' ? (
                      <Line
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        name={s.label}
                        stroke={colorFor(s.key, i)}
                        strokeWidth={2}
                        dot={false}
                        hide={isHidden(s.key)}
                      />
                    ) : (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        name={s.label}
                        stackId="energy"
                        fill={colorFor(s.key, i)}
                        hide={isHidden(s.key)}
                      />
                    )
                  ))}
                </ChartComponent>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Water chart */}
        {data && waterSeries.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Water (kL)</h2>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: 'kL', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
                  <Legend {...legendProps} />
                  {waterSeries.map((s, i) => (
                    chartType === 'line' ? (
                      <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={colorFor(s.key, i)} strokeWidth={2} dot={false} hide={isHidden(s.key)} />
                    ) : (
                      <Bar key={s.key} dataKey={s.key} name={s.label} stackId="water" fill={colorFor(s.key, i)} hide={isHidden(s.key)} />
                    )
                  ))}
                </ChartComponent>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Waste chart */}
        {data && wasteSeries.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Waste (MT)</h2>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: 'MT', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
                  <Legend {...legendProps} />
                  {wasteSeries.map((s, i) => (
                    chartType === 'line' ? (
                      <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={colorFor(s.key, i)} strokeWidth={2} dot={false} hide={isHidden(s.key)} />
                    ) : (
                      <Bar key={s.key} dataKey={s.key} name={s.label} stackId="waste" fill={colorFor(s.key, i)} hide={isHidden(s.key)} />
                    )
                  ))}
                </ChartComponent>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Non-energy native thermal (ton-h, kg) — kept distinct to avoid misleading totals */}
        {data && nativeThermal.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-1">
              Thermal — native units
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              ton-h and kg of steam can&apos;t be normalized to {data.target_unit} without enthalpy / refrigeration-rating data, so they&apos;re shown in their entered units.
            </p>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
                  <Legend {...legendProps} />
                  {nativeThermal.map((s, i) => (
                    chartType === 'line' ? (
                      <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={colorFor(s.key, i)} strokeWidth={2} dot={false} hide={isHidden(s.key)} />
                    ) : (
                      <Bar key={s.key} dataKey={s.key} name={s.label} fill={colorFor(s.key, i)} hide={isHidden(s.key)} />
                    )
                  ))}
                </ChartComponent>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Empty state */}
        {data && data.series.length === 0 && !loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500 text-sm">
            No data found for the selected filters. Try widening the period or selecting more energy types.
          </div>
        )}
      </div>
  );

  return embedded ? (
    <>
      {header}
      {body}
    </>
  ) : (
    <div className="min-h-screen bg-gray-50">
      {header}
      {body}
    </div>
  );
};

export default ERSTrends;
